import { Head, Link, router } from "@inertiajs/react"
import { Eye, Hash, MapPin, Sparkles, Users } from "lucide-react"
import { useState } from "react"

import FollowRecommendations from "@/components/feed/follow-recommendations"
import OpportunityFeedCard from "@/components/feed/opportunity-feed-card"
import PostCard from "@/components/feed/post-card"
import PostComposer from "@/components/feed/post-composer"
import ProfileCompletion from "@/components/feed/profile-completion"
import PushNotificationPrompt from "@/components/push-notification-prompt"
import StoryRow from "@/components/stories/story-row"
import StoryUploadDialog from "@/components/stories/story-upload-dialog"
import StoryViewer from "@/components/stories/story-viewer"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import AppLayout from "@/layouts/app-layout"
import type { FeedIndex as FeedIndexProps } from "@/types/serializers"

const POSITION_LABELS: Record<string, string> = {
  portero: "Portero",
  defensa_central: "Defensa Central",
  lateral_derecho: "Lateral Derecho",
  lateral_izquierdo: "Lateral Izquierdo",
  mediocampista_central: "Mediocampista Central",
  mediocampista_ofensivo: "Mediocampista Ofensivo",
  mediocampista_defensivo: "Mediocampista Defensivo",
  extremo_derecho: "Extremo Derecho",
  extremo_izquierdo: "Extremo Izquierdo",
  delantero_centro: "Delantero Centro",
  segundo_delantero: "Segundo Delantero",
}

const RECOMMENDATION_LABELS: Record<string, string> = {
  city: "De tu ciudad",
  region: "De tu provincia",
  country: "De tu país",
  popular: "Popular",
  recent: "Reciente",
  suggested: "Sugerido",
  highlight: "Highlight",
  hiring_potential: "Puede contratarte",
}

const TABS = [
  { key: "para_ti", label: "Para Ti" },
  { key: "siguiendo", label: "Siguiendo" },
] as const

export default function FeedIndex({
  posts,
  pagination,
  tab,
  profile_completion,
  stories,
  trending_players,
  popular_opportunities,
  trending_hashtags,
  feed_opportunities,
  suggested_users,
}: FeedIndexProps) {
  const [viewerOpen, setViewerOpen] = useState(false)
  const [viewerGroupIndex, setViewerGroupIndex] = useState(0)
  const [uploadOpen, setUploadOpen] = useState(false)

  const handleTabChange = (newTab: string) => {
    router.visit("/inicio", {
      data: { tab: newTab },
      preserveState: true,
    })
  }

  const handleViewStories = (groupIndex: number) => {
    setViewerGroupIndex(groupIndex)
    setViewerOpen(true)
  }

  const emptyStateMessage = () => {
    if (tab === "para_ti") {
      return (
        <>
          <p className="text-muted-foreground text-lg">No hay publicaciones</p>
          <p className="text-krown-grey-50 mt-1 text-sm">
            Completá tu perfil con tu ubicación para ver contenido relevante.
          </p>
          <Link
            href="/configuracion/perfil"
            className="bg-krown-200 hover:bg-krown-100 mt-3 inline-block rounded-full px-4 py-1.5 text-sm font-medium text-white transition-colors"
          >
            Completar perfil
          </Link>
        </>
      )
    }
    return (
      <>
        <p className="text-muted-foreground text-lg">No hay publicaciones</p>
        <p className="text-krown-grey-50 mt-1 text-sm">
          Seguí a otros usuarios para ver sus publicaciones aquí.
        </p>
      </>
    )
  }

  return (
    <AppLayout>
      <Head title="Inicio | KrownFT" />

      <div className="bg-background min-h-svh">
        <div className="mx-auto max-w-6xl px-4 py-6">
          {/* Push notification prompt */}
          <PushNotificationPrompt />

          {/* Stories row */}
          <StoryRow
            stories={stories}
            onViewStories={handleViewStories}
            onAddStory={() => setUploadOpen(true)}
          />

          {/* Profile Completion - mobile banner */}
          {profile_completion && profile_completion.percentage < 100 && (
            <div className="mb-4 lg:hidden">
              <ProfileCompletion
                percentage={profile_completion.percentage}
                checklist={profile_completion.checklist}
              />
            </div>
          )}

          {/* Tabs */}
          <div className="border-border mb-6 flex border-b">
            {TABS.map((t) => (
              <button
                key={t.key}
                onClick={() => handleTabChange(t.key)}
                className={`relative px-5 py-3 text-xs font-semibold tracking-wider uppercase transition-colors ${
                  tab === t.key
                    ? "text-white"
                    : "text-muted-foreground hover:text-white"
                }`}
              >
                {t.label}
                {tab === t.key && (
                  <div className="bg-krown-accent absolute bottom-0 left-0 h-[3px] w-full" />
                )}
              </button>
            ))}
          </div>

          <div className="flex gap-6">
            {/* Main feed column */}
            <div className="min-w-0 flex-1">
              {/* Trending sections on Para Ti tab */}
              {tab === "para_ti" && (
                <>
                  {trending_players && trending_players.length > 0 && (
                    <div className="mb-6">
                      <h2 className="text-muted-foreground mb-3 text-sm font-bold tracking-wider uppercase">
                        Jugadores Destacados
                      </h2>
                      <div className="scrollbar-hide flex gap-3 overflow-x-auto pb-2">
                        {trending_players.map((player) => (
                          <Link
                            key={player.id}
                            href={`/jugadores/${player.slug}`}
                            className="border-border bg-card hover:border-krown-300 max-w-[180px] min-w-[160px] flex-shrink-0 rounded-lg border p-3 transition-colors"
                          >
                            <Avatar className="mb-2 h-10 w-10">
                              <AvatarImage
                                src={player.user.avatar_url ?? undefined}
                                alt={player.user.full_name}
                              />
                              <AvatarFallback className="bg-krown-200 text-sm font-bold text-white">
                                {player.user.first_name[0]}
                                {player.user.last_name[0]}
                              </AvatarFallback>
                            </Avatar>
                            <p className="truncate text-sm font-semibold text-white">
                              {player.user.full_name}
                            </p>
                            <p className="text-muted-foreground text-xs">
                              {POSITION_LABELS[player.position] ??
                                player.position}
                            </p>
                            <div className="text-krown-grey-50 mt-1 flex items-center gap-1 text-xs">
                              <MapPin className="h-3 w-3" />
                              <span className="truncate">
                                {player.city}, {player.country}
                              </span>
                            </div>
                            <div className="bg-krown-200/20 text-krown-100 mt-1.5 flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium">
                              <Eye className="h-3 w-3" />
                              {player.views_count} visitas
                            </div>
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}

                  {popular_opportunities &&
                    popular_opportunities.length > 0 && (
                      <div className="mb-6">
                        <h2 className="text-muted-foreground mb-3 text-sm font-bold tracking-wider uppercase">
                          Oportunidades Populares
                        </h2>
                        <div className="scrollbar-hide flex gap-3 overflow-x-auto pb-2">
                          {popular_opportunities.map((opp) => (
                            <Link
                              key={opp.id}
                              href={`/oportunidades/${opp.id}`}
                              className="border-border bg-card hover:border-krown-300 max-w-[240px] min-w-[200px] flex-shrink-0 rounded-lg border p-3 transition-colors"
                            >
                              <p className="truncate text-sm font-semibold text-white">
                                {opp.title}
                              </p>
                              <p className="text-krown-100 text-xs">
                                {opp.club_profile.name}
                              </p>
                              <p className="text-muted-foreground mt-1 text-xs">
                                {POSITION_LABELS[opp.position_needed] ??
                                  opp.position_needed}
                              </p>
                              <div className="text-krown-grey-50 mt-1.5 flex items-center gap-1 text-xs">
                                <Users className="h-3 w-3" />
                                {opp.applications_count} postulaciones
                              </div>
                            </Link>
                          ))}
                        </div>
                      </div>
                    )}

                  {trending_hashtags && trending_hashtags.length > 0 && (
                    <div className="mb-6">
                      <h2 className="text-muted-foreground mb-3 text-sm font-bold tracking-wider uppercase">
                        Trending
                      </h2>
                      <div className="flex flex-wrap gap-2">
                        {trending_hashtags.map((hashtag) => (
                          <Link
                            key={hashtag.id}
                            href={`/buscar?hashtag=${encodeURIComponent(hashtag.name)}`}
                            className="border-border bg-card text-krown-100 hover:border-krown-300 inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm font-medium transition-colors"
                          >
                            <Hash className="h-3.5 w-3.5" />#{hashtag.name}
                            <span className="text-krown-grey-50 text-xs">
                              {hashtag.posts_count}
                            </span>
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* Post Composer */}
              <PostComposer />

              {/* Posts with interleaved opportunity cards */}
              {posts.length > 0 ? (
                <>
                  {posts.map((post, index) => {
                    const oppInterval = Math.max(
                      3,
                      Math.floor(
                        posts.length / (feed_opportunities.length + 1),
                      ),
                    )
                    const oppIndex = Math.floor((index + 1) / oppInterval) - 1
                    const showOpp =
                      (index + 1) % oppInterval === 0 &&
                      index > 0 &&
                      feed_opportunities.length > 0 &&
                      oppIndex >= 0 &&
                      oppIndex < feed_opportunities.length

                    return (
                      <div key={post.id}>
                        {tab === "para_ti" && post.recommendation_reason && (
                          <div className="flex items-center gap-1.5 px-4 pt-3 pb-0">
                            <Sparkles className="text-krown-crown h-3 w-3" />
                            <span className="text-krown-crown text-[11px] font-medium tracking-wider uppercase">
                              {RECOMMENDATION_LABELS[
                                post.recommendation_reason
                              ] ?? post.recommendation_reason}
                            </span>
                          </div>
                        )}
                        <PostCard post={post} showViewAllComments={true} />
                        {index === 2 && suggested_users?.length > 0 && (
                          <FollowRecommendations
                            suggestions={suggested_users}
                          />
                        )}
                        {showOpp && (
                          <OpportunityFeedCard
                            opportunity={feed_opportunities[oppIndex]}
                          />
                        )}
                      </div>
                    )
                  })}

                  {/* Pagination */}
                  {pagination.pages > 1 && (
                    <div className="mt-6 flex justify-center gap-2">
                      {pagination.page > 1 && (
                        <button
                          onClick={() =>
                            router.visit("/inicio", {
                              data: { tab, page: pagination.page - 1 },
                              preserveState: true,
                            })
                          }
                          className="bg-card hover:bg-muted rounded-full px-4 py-1.5 text-sm text-white"
                        >
                          Anterior
                        </button>
                      )}
                      <span className="text-muted-foreground px-3 py-1.5 text-sm">
                        Página {pagination.page} de {pagination.pages}
                      </span>
                      {pagination.page < pagination.pages && (
                        <button
                          onClick={() =>
                            router.visit("/inicio", {
                              data: { tab, page: pagination.page + 1 },
                              preserveState: true,
                            })
                          }
                          className="bg-card hover:bg-muted rounded-full px-4 py-1.5 text-sm text-white"
                        >
                          Siguiente
                        </button>
                      )}
                    </div>
                  )}
                </>
              ) : (
                <div className="py-16 text-center">{emptyStateMessage()}</div>
              )}
            </div>

            {/* Sidebar - desktop only */}
            {profile_completion && profile_completion.percentage < 100 && (
              <aside className="hidden w-72 flex-shrink-0 lg:block">
                <div className="sticky top-20">
                  <ProfileCompletion
                    percentage={profile_completion.percentage}
                    checklist={profile_completion.checklist}
                  />
                </div>
              </aside>
            )}
          </div>
        </div>
      </div>

      {/* Story Viewer */}
      {viewerOpen && stories.length > 0 && (
        <StoryViewer
          storyGroups={stories}
          initialGroupIndex={viewerGroupIndex}
          onClose={() => setViewerOpen(false)}
        />
      )}

      {/* Story Upload Dialog */}
      <StoryUploadDialog
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
      />
    </AppLayout>
  )
}
